/**
 * Player switching. (§4.8)
 *
 * ── WIRING (orchestrator) — the ONLY main.ts touches this file needs ─────────
 *
 * In bootWorld, once the Welcome (`p`) and the prediction/switcher exist:
 *
 *   applyRosterToWorld(template, p.roster);              // BEFORE new PredictionSystem
 *   const switcher = new PlayerSwitcher({ initialPlayerId: p.assignedPlayerId,
 *                                         team: p.team, enabled: p.switchingEnabled });
 *   wireSwitching({ connection: conn, prediction, switcher });
 *
 * `applyRosterToWorld` is imported from '@fg/shared/net'; `wireSwitching` from
 * this file. That is the whole integration: the server drives the switch and
 * ControlAssign, `wireSwitching` routes it into prediction (rebase) and this
 * switcher (camera blend). For OFFLINE modes (Practice vs COM) there is no
 * server, so the caller drives `requestManual` / `updateAuto` directly — both
 * are still here and now share the server's selection math.
 *
 * ── USER-FLAGGED UX BAR (SESSION_LOG 2026-07-17) ────────────────────────────
 *
 * "solo play with AI teammates must feel smooth like FIFA — camera/control blend
 * on switch, not an instant snap." §4.8 specs the auto/manual selection; the
 * blend is the added bar, and it is the whole difference between this feeling
 * like FIFA and feeling like a debug key. Control transfers instantly (input has
 * to go somewhere); the CAMERA travels over SWITCH_BLEND_MS.
 *
 * ── The contract gap is CLOSED (was: "server ignores SwitchPlayer") ─────────
 *
 * This file's old header warned that nothing on the server read
 * InputFlags.SwitchPlayer and no frame re-stated the driven id, so switching
 * shipped disabled. Both are fixed this wave: the server honours SwitchPlayer +
 * auto-switches (server/room.ts), echoes ControlAssign (net/control.ts), and the
 * roster is on the wire so positions are real (net/welcome.ts) — so GK exclusion
 * actually fires. `confirmControlled()` is now the authoritative path, and the
 * selection math is shared with the server via shared/src/switching.ts (no more
 * duplicate cone-scoring that could disagree with the authority). Switching is
 * enabled by default now, gated only on `welcome.switchingEnabled` (§4.8: off
 * for a full-human team).
 *
 * Pure logic — no Three.js, no DOM.
 */

import {
  AUTO_SWITCH_INPUT_GRACE_MS,
  SWITCH_BLEND_MS,
  type WorldState,
} from '@fg/shared/sim';
import { selectSwitchTarget } from '@fg/shared/switching';
import { clamp01 } from '@fg/shared/math';

/** Smoothstep — eases both ends. A linear blend still reads as a mechanical slide. */
function smoothstep(t: number): number {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}

export interface PlayerSwitchOptions {
  /** The seat the Welcome assigned. The starting belief. */
  initialPlayerId: number;
  team: number;
  /**
   * §4.8: OFF for a full-human team ("you are your player"). Now that the server
   * honours the switch and echoes ControlAssign, this defaults ON — it is gated
   * on `welcome.switchingEnabled`, which the server sets false only for a
   * full-human team. (Was default OFF while the server ignored the flag.)
   */
  enabled?: boolean;
}

export class PlayerSwitcher {
  /** The PlayerId this client believes it drives. */
  private controlled: number;
  private readonly team: number;
  enabled: boolean;

  /** Where the camera should look FROM during a blend: the player we left. */
  private prevControlled = -1;
  /** 0..1 through the switch blend. 1 = settled. */
  private blendT = 1;

  private lastPossessionTeam = -2; // -2 = "never seen a snapshot yet"
  private lastMovedAtMs = -1;

  constructor(opts: PlayerSwitchOptions) {
    this.controlled = opts.initialPlayerId;
    this.team = opts.team;
    this.enabled = opts.enabled ?? true;
  }

  get controlledId(): number {
    return this.controlled;
  }

  /** 0..1 through the current switch blend — drives the indicator ring. */
  get blend(): number {
    return this.blendT;
  }

  get switching(): boolean {
    return this.blendT < 1;
  }

  /**
   * The server told us which player we drive (ControlAssign, §4.8). Authoritative
   * — this is the path `wireSwitching` fires, and it begins the camera blend so
   * the handoff is smooth whether the switch was our own optimistic flick, an
   * auto-switch, or a drop-in.
   */
  confirmControlled(id: number): void {
    if (id === this.controlled) return;
    this.beginSwitch(id);
  }

  /** Record that the human is actively steering — gates auto-switch (§4.8). */
  noteMovement(moveX: number, moveZ: number, nowMs: number): void {
    // A dead-stick reading is not "movement". The threshold is well above
    // analog noise and well below a deliberate nudge.
    if (Math.hypot(moveX, moveZ) > 0.2) this.lastMovedAtMs = nowMs;
  }

  private beginSwitch(next: number): void {
    this.prevControlled = this.controlled;
    this.controlled = next;
    this.blendT = 0;
  }

  /** Advance the blend clock. Call once per rendered frame. */
  tick(dtMs: number): void {
    if (this.blendT >= 1) return;
    this.blendT = clamp01(this.blendT + dtMs / SWITCH_BLEND_MS);
    if (this.blendT >= 1) this.prevControlled = -1;
  }

  /**
   * Auto-switch (§4.8): when the OPPONENT wins the ball, hand control to whoever
   * on our side is nearest it — unless the human has been steering in the last
   * AUTO_SWITCH_INPUT_GRACE_MS. ONLINE this is the server's job (it echoes
   * ControlAssign → confirmControlled); this method is for OFFLINE play (Practice
   * vs COM), where there is no server and the client is the authority. Selection
   * is shared/switching.ts, the same code the server runs.
   *
   * @returns the new controlled id if it switched, else -1.
   */
  updateAuto(world: WorldState, nowMs: number): number {
    if (!this.enabled) return -1;

    const possession = world.match.possessionTeam;
    const prev = this.lastPossessionTeam;
    this.lastPossessionTeam = possession;

    if (prev === -2) return -1; // first observation — a "change" here is bogus
    if (possession === prev) return -1;
    // Only react to the opponent gaining it. Our own team winning the ball is
    // handled by the player who won it; switching then would steal control from
    // the human who just made the tackle.
    if (possession === this.team || possession < 0) return -1;

    if (nowMs - this.lastMovedAtMs < AUTO_SWITCH_INPUT_GRACE_MS) return -1;

    // Null direction → nearest ball-relevant. Includes the current player as a
    // candidate, so "already on the nearest" comes back as `controlled` → no-op.
    const nearest = selectSwitchTarget(world, this.team, this.controlled, null);
    if (nearest < 0 || nearest === this.controlled) return -1;
    this.beginSwitch(nearest);
    return nearest;
  }

  /**
   * Manual switch (§4.8): cone-based selection in the flicked direction, weighted
   * by distance and relevance to the ball. ONLINE the client also raises
   * InputFlags.SwitchPlayer so the server makes it authoritative (and the two
   * agree, both using switching.ts); OFFLINE this IS the authority. A near-zero
   * direction (a directionless Tab-tap) falls through to nearest ball-relevant.
   *
   * @param dirX,dirZ  The flick direction in world XZ. Need not be normalized.
   * @returns the new controlled id if it switched, else -1.
   */
  requestManual(world: WorldState, dirX: number, dirZ: number): number {
    if (!this.enabled) return -1;
    const dir = Math.hypot(dirX, dirZ) < 1e-3 ? null : { x: dirX, z: dirZ };
    const best = selectSwitchTarget(world, this.team, this.controlled, dir);
    if (best < 0 || best === this.controlled) return -1;
    this.beginSwitch(best);
    return best;
  }

  /**
   * The position the CAMERA should follow: eased from the player we left to the
   * one we took, over SWITCH_BLEND_MS. This is the UX bar — control transfers
   * instantly (input has to go somewhere) but the frame travels, so the player
   * keeps their bearings instead of being teleported across the pitch.
   */
  blendedPosition(world: WorldState, out: { x: number; y: number; z: number }): void {
    const p = world.players;
    const cur = this.controlled;
    const cx = p.px[cur] ?? 0;
    const cy = p.py[cur] ?? 0;
    const cz = p.pz[cur] ?? 0;

    if (this.blendT >= 1 || this.prevControlled < 0) {
      out.x = cx; out.y = cy; out.z = cz;
      return;
    }

    const prev = this.prevControlled;
    const t = smoothstep(this.blendT);
    out.x = (p.px[prev] ?? cx) + (cx - (p.px[prev] ?? cx)) * t;
    out.y = (p.py[prev] ?? cy) + (cy - (p.py[prev] ?? cy)) * t;
    out.z = (p.pz[prev] ?? cz) + (cz - (p.pz[prev] ?? cz)) * t;
  }
}

// ---------------------------------------------------------------------------
// Wiring — the one call main.ts makes (§4.8)
// ---------------------------------------------------------------------------

/** The bit of GameConnection wireSwitching needs — set the ControlAssign hook. */
export interface ControlAssignSource {
  onControlAssign: ((playerId: number) => void) | null;
}

/** The bit of PredictionSystem wireSwitching needs — rebase onto a new id. */
export interface RebasablePrediction {
  rebaseControlled(playerId: number): void;
}

/**
 * Route the server's authoritative ControlAssign (§4.8) into prediction and the
 * camera blend. After this, a switch the server honours — a manual flick, an
 * auto-switch on a possession change, or a drop-in — rebases prediction onto the
 * new PlayerId (no NaN, no teleport) and eases the camera across. This is the
 * whole online integration; call it once, after prediction and the switcher exist.
 */
export function wireSwitching(params: {
  connection: ControlAssignSource;
  prediction: RebasablePrediction;
  switcher: PlayerSwitcher;
}): void {
  params.connection.onControlAssign = (playerId: number): void => {
    params.prediction.rebaseControlled(playerId);
    params.switcher.confirmControlled(playerId);
  };
}
